import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Textarea from '../../common/Textarea';
import Button from '../../common/Button';
import FileUpload from '../../common/FileUpload';
import { TICKET_PRIORITIES, TICKET_TYPES, TICKET_STATUSES, TICKET_TYPE_LABELS, TYPES_REQUIRING_NO_SELECTION } from '../../../constants/statuses';
import { validateRequired } from '../../../utils/validators';

const EditTicketModal = ({ isOpen, onClose, onSubmit, ticket }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    system_type: '',
    ticket_type: '',
    priority: '',
    status: '',
    site_id: '',
    department_id: ''
  });

  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ticket) {
      setFormData({
        title: ticket.title || '',
        description: ticket.description || '',
        system_type: ticket.system_type || '',
        ticket_type: ticket.ticket_type || '',
        priority: ticket.priority || '',
        status: ticket.status || '',
        site_id: ticket.site_id?.toString() || '',
        department_id: ticket.department_id?.toString() || ''
      });
    }
  }, [ticket]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      if (name === 'ticket_type') {
        const wasPM = TYPES_REQUIRING_NO_SELECTION.includes(prev.ticket_type);
        const isNowPM = TYPES_REQUIRING_NO_SELECTION.includes(value);
        if (wasPM !== isNowPM) {
          updatedData.system_type = '';
          updatedData.problem_type = '';
        }
      }
      return updatedData;
    });
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileUpload = async (file) => {
    setAttachments(prev => [...prev, {
      file: file,
      name: file.name,
      size: file.size,
      type: file.type
    }]);
  };

  const handleRemoveFile = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '';
    if (mimeType?.startsWith('video/')) return '';
    if (mimeType?.includes('pdf')) return '';
    if (mimeType?.includes('word')) return '';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '';
    return '';
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.title)) {
      newErrors.title = 'Title is required';
    }
    const isPM = TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type);
    if (!isPM && !validateRequired(formData.system_type)) {
      newErrors.system_type = 'System type is required';
    }
    if (!validateRequired(formData.ticket_type)) {
      newErrors.ticket_type = 'Ticket type is required';
    }
    if (!validateRequired(formData.priority)) {
      newErrors.priority = 'Priority is required';
    }
    if (!validateRequired(formData.status)) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setSubmitting(true);

    const isPM = TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type);
    const updateData = { ...formData };
    if (isPM) {
      if (!updateData.system_type) updateData.system_type = ticket?.system_type || 'Preventive Maintenance';
      if (!updateData.problem_type) updateData.problem_type = ticket?.problem_type || 'Scheduled PM';
    }

    try {
      await onSubmit(ticket.id, updateData, attachments);
      onClose();
    } catch (error) {
      console.error('Error updating ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      setAttachments([]);
      onClose();
    }
  };

  const systemTypes = [
    { value: 'HVAC', label: 'HVAC System' },
    { value: 'Fire Alarm', label: 'Fire Alarm System' },
    { value: 'Access Control', label: 'Access Control System' },
    { value: 'CCTV', label: 'CCTV System' },
    { value: 'Elevator', label: 'Elevator System' },
    { value: 'Generator', label: 'Generator System' },
    { value: 'Electrical', label: 'Electrical System' },
    { value: 'Plumbing', label: 'Plumbing System' }
  ];

  const sites = [
    { value: '1', label: 'Site A - Tech Park' },
    { value: '2', label: 'Site B - Main Office' },
    { value: '3', label: 'Site C - Warehouse' },
    { value: '4', label: 'Site D - Retail Store' }
  ];

  const departments = [
    { value: '1', label: 'Administration' },
    { value: '2', label: 'Field Operations' },
    { value: '3', label: 'Technical Support' }
  ];

  const priorityOptions = Object.values(TICKET_PRIORITIES).map(p => ({
    value: p,
    label: p
  }));

  const ticketTypeOptions = Object.values(TICKET_TYPES).map(t => ({
    value: t,
    label: TICKET_TYPE_LABELS[t] || t
  }));

  const statusOptions = Object.values(TICKET_STATUSES).map(s => ({
    value: s,
    label: s.replace('_', ' ')
  }));

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    marginTop: '20px'
  };

  const attachmentListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '12px'
  };

  const attachmentItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--surface-light)',
    borderRadius: '8px',
    border: '1px solid var(--border)'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update Ticket'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Ticket #${ticket?.id}`}
      size="large"
      footer={footer}
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="Ticket Title"
          name="title"
          placeholder="e.g., HVAC System Not Working"
          value={formData.title}
          onChange={handleChange}
          error={errors.title}
          required
        />

        <Textarea
          label="Description"
          name="description"
          placeholder="Provide detailed information about the issue..."
          value={formData.description}
          onChange={handleChange}
          rows={4}
        />

        <div style={gridStyle}>
          <Select
            label="System Type"
            name="system_type"
            value={formData.system_type}
            onChange={handleChange}
            options={systemTypes}
            error={errors.system_type}
            required={!TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
            disabled={TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
            placeholder={TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type) ? "-- Not required for PM --" : "Select system type"}
          />
        </div>

        <div style={gridStyle}>
          <Select
            label="Ticket Type"
            name="ticket_type"
            value={formData.ticket_type}
            onChange={handleChange}
            options={ticketTypeOptions}
            error={errors.ticket_type}
            required
          />

          <Select
            label="Priority"
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            options={priorityOptions}
            error={errors.priority}
            required
          />
        </div>

        <div style={gridStyle}>
          <Select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={statusOptions}
            error={errors.status}
            required
          />

          <Select
            label="Site"
            name="site_id"
            value={formData.site_id}
            onChange={handleChange}
            options={sites}
            required
          />
        </div>

        {/* Attachments Section */}
        <div style={sectionTitleStyle}> Add New Attachments (Optional)</div>
        <FileUpload
          onUpload={handleFileUpload}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          maxSize={10485760}
          multiple={false}
        />

        {attachments.length > 0 && (
          <div style={attachmentListStyle}>
            {attachments.map((attachment, index) => (
              <div key={index} style={attachmentItemStyle}>
                <div style={{ fontSize: '32px', flexShrink: 0 }}>
                  {getFileIcon(attachment.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {attachment.name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {formatFileSize(attachment.size)}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                >
                   Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        {ticket?.metrics?.attachment_count > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#3b82f620',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#3b82f6',
            marginTop: '12px'
          }}>
             This ticket already has {ticket.metrics.attachment_count} existing attachment(s). New files will be added.
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditTicketModal;